/**
 * KND-4b: e-mailing one termination confirmation version (KND-4a endpoints).
 *
 * Two calls, nothing else: ask the server what a send would do now (the
 * preview), and ask it to send. Recipient, sender, subject, body and the
 * attachment are the server's — this client never names any of them. The
 * only thing a send request can carry is `previous_delivery_id`: null for a
 * first send, the version's latest attempt for a deliberate resend. The
 * delivery history itself arrives with the confirmations GET
 * (`versions[].deliveries`), not from here.
 *
 * A failed request throws an Error with the HTTP `status`, the server's
 * controlled `code` (`detail.code`) and, for a refused send, its
 * `errorCode` (`detail.error_code`). Callers show their own copy for these
 * codes; the server's sentence is kept on `message` only as a fallback.
 */
import { API_BASE_URL, getApiHeaders, parseAdminErrorFromText } from "./core";

function confirmationUrl(confirmationId, suffix) {
  return `${API_BASE_URL}/api/admin/termination-confirmations/${encodeURIComponent(
    confirmationId
  )}/${suffix}`;
}

async function deliveryError(res, fallback) {
  let text = "";
  try {
    text = await res.text();
  } catch (_) {
    text = "";
  }
  const err = new Error(
    text ? parseAdminErrorFromText(text, res.status, res.statusText) : fallback
  );
  err.status = res.status;
  try {
    const detail = JSON.parse(text)?.detail;
    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      if (typeof detail.code === "string") err.code = detail.code;
      if (typeof detail.error_code === "string") err.errorCode = detail.error_code;
    }
  } catch (_) {
    // A non-JSON body carries no code; the caller falls back to the status.
  }
  return err;
}

/** GET /api/admin/termination-confirmations/{confirmationId}/email-preview */
export async function fetchAdminTerminationConfirmationEmailPreview(confirmationId) {
  const res = await fetch(confirmationUrl(confirmationId, "email-preview"), {
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    throw await deliveryError(res, "Versandvorschau konnte nicht geladen werden.");
  }
  return res.json();
}

/**
 * POST /api/admin/termination-confirmations/{confirmationId}/deliveries
 *
 * `previousDeliveryId` null — a first send; set — the latest attempt this
 * client knows of, so a resend the server no longer expects is refused (409)
 * instead of producing a second attempt.
 */
export async function createAdminTerminationConfirmationDelivery(
  confirmationId,
  { previousDeliveryId = null } = {}
) {
  const res = await fetch(confirmationUrl(confirmationId, "deliveries"), {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify({ previous_delivery_id: previousDeliveryId || null }),
  });
  if (!res.ok) {
    throw await deliveryError(res, "Versand konnte nicht ausgeführt werden.");
  }
  return res.json();
}
